import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CommentCard from './CommentCard';
import CreateComment from './CreateComment';
import BottomSheet from './BottomSheet';
import { useSocket } from '../contexts/SocketContext';

const CommentsModal = ({ postId, isOpen, onClose, onCommentAdded, postAuthorId }) => {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const socket = useSocket();

  useEffect(() => {
    if (isOpen && postId) {
      fetchComments();
    }
  }, [isOpen, postId]);

  useEffect(() => {
    if (socket && isOpen) {
      socket.on('new-comment', (data) => {
        if (data.postId === postId) {
          fetchComments();
          if (onCommentAdded) onCommentAdded();
        }
      });

      socket.on('post-deleted', (data) => {
        if (data.postId) {
          setComments(prev => prev.filter(c => c.id !== data.postId));
        }
      });

      return () => {
        socket.off('new-comment');
        socket.off('post-deleted');
      };
    }
  }, [socket, postId, isOpen, onCommentAdded]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/posts/${postId}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Fetch comments error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentDeleted = (commentId) => {
    setComments(prev => prev.filter(c => c.id !== commentId));
  };

  if (!isOpen) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Comments"
      maxHeight="max-h-[85vh] h-[80vh] sm:h-auto sm:max-h-[85vh]"
    >
      <div className="flex flex-col h-full">
        {/* Create Comment */}
        <div className="mb-4 shrink-0">
          <div className="bg-gray-50 dark:bg-gray-800/70 rounded-2xl p-3 border border-gray-100 dark:border-gray-800">
            <CreateComment postId={postId} onCommentCreated={fetchComments} />
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-3 border-gray-200 dark:border-gray-700 border-t-sky-500 rounded-full animate-spin mb-3"></div>
              <p className="text-gray-400 dark:text-gray-500 text-sm">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center mb-3">
                <i className="fas fa-comments text-gray-400 dark:text-gray-500 text-xl"></i>
              </div>
              <p className="text-gray-600 dark:text-gray-300 font-semibold mb-1">No comments yet</p>
              <p className="text-gray-400 dark:text-gray-500 text-xs text-center max-w-xs">
                Be the first to share your thoughts!
              </p>
            </div>
          ) : (
            <div className="space-y-1 pb-4">
              <div className="flex items-center gap-2 mb-2 px-1">
                <i className="fas fa-comments text-gray-400 dark:text-gray-500 text-xs"></i>
                <span className="text-gray-600 dark:text-gray-400 font-medium text-xs">
                  {comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}
                </span>
              </div>
              {comments.map((comment) => (
                <CommentCard
                  key={comment.id}
                  comment={comment}
                  postId={postId}
                  postAuthorId={postAuthorId}
                  onUpdate={fetchComments}
                  onDelete={handleCommentDeleted}
                  onCommentClick={(commentId) => {
                    onClose();
                    navigate(`/post/${postId}/comment/${commentId}`);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  );
};

export default CommentsModal;
